import React, { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";

import "minireset.css";
import "./index.css";
import { BrowserRouter, useHistory, useLocation } from "react-router-dom";

import { installTwicpics } from "@twicpics/components/react";
import "@twicpics/components/style.css";

import {
  bugsnagAppVersion,
  bugsnagApiKey,
  environmentName,
  isProd,
  twicpicsUrl,
} from "./config";

import { App } from "./App";
import { isIgnoredError } from "./utils/errors";
// Imported directly rather than through the errors barrel: the fallback is
// deliberately dependency-free, and the barrel would pull NotFoundError and
// its video/ccl imports into the entry module's init path.
import { AppErrorFallback } from "./components/errors/AppErrorFallback";
import { isTrackingDisabled, startAmplitude } from "./utils/tracking";

if (twicpicsUrl) {
  installTwicpics({
    domain: twicpicsUrl,
    anticipation: 0.5,
  });
}

if (!isTrackingDisabled()) {
  // Error reporting is the one vendor that starts before consent, as strictly
  // necessary: it stores nothing on the device and sends only when the app
  // breaks. Session counts are stability analytics, so they wait for consent.
  // See docs/decisions/2026-10-10-gloss-cookie-consent.md.
  Bugsnag.start({
    apiKey: bugsnagApiKey,
    appVersion: bugsnagAppVersion,
    plugins: [new BugsnagPluginReact(React)],
    releaseStage: environmentName,
    enabledReleaseStages: ["production", "staging"],
    autoTrackSessions: false,
    generateAnonymousId: false,
    redactedKeys: [
      /^password$/i,
      /^account_?number$/i,
      /^sort_?code$/i,
      /^iban$/i,
      /^owner_?name$/i,
      /^verification_?(code|session)$/i,
      /^challenge_?id$/i,
      /^query$/i,
      /^variables$/i,
    ],
    onError: function (event) {
      if (isIgnoredError(event)) {
        return false;
      }
    },
  });

  window.gloss?.whenConsented?.("analytics", () => {
    Bugsnag.startSession();
    startAmplitude();
  });
}

const BugsnagErrorBoundary =
  (isProd &&
    !isTrackingDisabled() &&
    Bugsnag.getPlugin("react")?.createErrorBoundary(React)) ||
  null;

class CredentialRouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? <AppErrorFallback /> : this.props.children;
  }
}

// Outside production there is no boundary, so errors surface loudly in the
// console. Credential routes retain the fallback without starting Bugsnag.
const ErrorBoundary = ({ children }: { children: React.ReactNode }) =>
  BugsnagErrorBoundary ? (
    <BugsnagErrorBoundary FallbackComponent={AppErrorFallback}>
      {children}
    </BugsnagErrorBoundary>
  ) : isProd && isTrackingDisabled() ? (
    <CredentialRouteErrorBoundary>{children}</CredentialRouteErrorBoundary>
  ) : (
    <>{children}</>
  );

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const history = useHistory();
  useEffect(() => {
    // the browser remembers scroll positions when you go back
    // we don't want to break that
    if (history.action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
};

const container = document.getElementById("root");

if (!container) throw new Error("container not found!");

const root = createRoot(container);

root.render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
